import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  Webhook as WebhookIcon, Plus, Trash2, RefreshCw, Send, Copy, ChevronDown, ChevronUp, Circle
} from 'lucide-react';
import api from '../services/api';

const ALL_EVENTS = [
  'lead.created',
  'lead.updated',
  'lead.stage_changed',
  'note.added',
  'task.completed',
  'email.opened',
  'email.clicked'
];

const EMPTY_FORM = { name: '', url: '', events: ['lead.created'], isActive: true };

const StatusBadge = ({ status }) => {
  const map = {
    success: 'bg-green-100 text-green-700',
    failed:  'bg-red-100 text-red-700',
    pending: 'bg-amber-100 text-amber-700'
  };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

const WebhookSettings = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [visibleSecret, setVisibleSecret] = useState(null);

  const { data: webhooks = [], isLoading } = useQuery(
    ['webhooks'],
    () => api.get('/webhooks').then((r) => r.data)
  );

  const createMutation = useMutation(
    (body) => api.post('/webhooks', body).then((r) => r.data),
    {
      onSuccess: () => { toast.success('Webhook created'); queryClient.invalidateQueries(['webhooks']); resetForm(); },
      onError: (err) => toast.error(err.response?.data?.error || 'Create failed')
    }
  );

  const patchMutation = useMutation(
    ({ id, patch }) => api.patch(`/webhooks/${id}`, patch).then((r) => r.data),
    {
      onSuccess: () => { toast.success('Saved'); queryClient.invalidateQueries(['webhooks']); resetForm(); },
      onError: (err) => toast.error(err.response?.data?.error || 'Update failed')
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/webhooks/${id}`).then((r) => r.data),
    { onSuccess: () => { toast.success('Deleted'); queryClient.invalidateQueries(['webhooks']); } }
  );

  const toggleActiveMutation = useMutation(
    ({ id, isActive }) => api.patch(`/webhooks/${id}`, { isActive }).then((r) => r.data),
    { onSuccess: () => queryClient.invalidateQueries(['webhooks']) }
  );

  const rotateMutation = useMutation(
    (id) => api.post(`/webhooks/${id}/rotate-secret`).then((r) => r.data),
    {
      onSuccess: (data, id) => {
        toast.success('Secret rotated');
        setVisibleSecret({ id, secret: data.secret });
        queryClient.invalidateQueries(['webhooks']);
      }
    }
  );

  const testMutation = useMutation(
    (id) => api.post(`/webhooks/${id}/test`).then((r) => r.data),
    {
      onSuccess: (delivery) => {
        toast.success(`Test: ${delivery.status}${delivery.responseCode ? ` (${delivery.responseCode})` : ''}`);
        queryClient.invalidateQueries(['webhook-deliveries', delivery.webhookId]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Test failed')
    }
  );

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (hook) => {
    setForm({ name: hook.name, url: hook.url, events: hook.events, isActive: hook.isActive });
    setEditingId(hook._id);
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) return toast.error('Name and URL are required');
    if (form.events.length === 0) return toast.error('Select at least one event');
    if (editingId) {
      patchMutation.mutate({ id: editingId, patch: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const toggleEvent = (ev) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((x) => x !== ev) : [...f.events, ev]
    }));
  };

  const copyToClipboard = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success('Copied');
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <WebhookIcon size={28} /> Webhooks
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Forward events to Zapier, Make, n8n or your own endpoint. Payloads are signed with HMAC-SHA256.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
            className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
          >
            <Plus size={14} /> New Webhook
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6 space-y-4">
          <h2 className="text-lg font-semibold">{editingId ? 'Edit webhook' : 'New webhook'}</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Slack alerts"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
            <input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://hooks.zapier.com/..."
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ALL_EVENTS.map((ev) => (
                <label key={ev} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.events.includes(ev)} onChange={() => toggleEvent(ev)} />
                  <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded">{ev}</code>
                </label>
              ))}
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            Active
          </label>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={resetForm} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button
              type="submit"
              disabled={createMutation.isLoading || patchMutation.isLoading}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
            >
              {editingId ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : webhooks.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-8 text-center">
          <WebhookIcon size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">No webhooks yet. Create one to start forwarding events.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((hook) => (
            <WebhookCard
              key={hook._id}
              hook={hook}
              expanded={expandedId === hook._id}
              onToggle={() => setExpandedId(expandedId === hook._id ? null : hook._id)}
              onEdit={() => startEdit(hook)}
              onDelete={() => window.confirm('Delete this webhook?') && deleteMutation.mutate(hook._id)}
              onRotateSecret={() => window.confirm('Rotate secret? The old one will stop working immediately.') && rotateMutation.mutate(hook._id)}
              onTest={() => testMutation.mutate(hook._id)}
              onToggleActive={() => toggleActiveMutation.mutate({ id: hook._id, isActive: !hook.isActive })}
              visibleSecret={visibleSecret?.id === hook._id ? visibleSecret.secret : null}
              onCopy={copyToClipboard}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const WebhookCard = ({ hook, expanded, onToggle, onEdit, onDelete, onRotateSecret, onTest, onToggleActive, visibleSecret, onCopy }) => {
  const { data: deliveries = [] } = useQuery(
    ['webhook-deliveries', hook._id],
    () => api.get(`/webhooks/${hook._id}/deliveries`).then((r) => r.data),
    { enabled: expanded, refetchInterval: expanded ? 8000 : false }
  );

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 p-4">
        <Circle size={10} className={hook.isActive ? 'text-green-500 fill-green-500' : 'text-gray-300 fill-gray-300'} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{hook.name}</h3>
            {!hook.isActive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Paused</span>}
          </div>
          <p className="text-xs text-gray-500 font-mono truncate">{hook.url}</p>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {hook.events.map((ev) => (
              <code key={ev} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{ev}</code>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onTest} title="Send test" className="p-2 text-gray-500 hover:text-primary-600">
            <Send size={16} />
          </button>
          <button onClick={onToggleActive} title={hook.isActive ? 'Pause' : 'Resume'} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900">
            {hook.isActive ? 'Pause' : 'Resume'}
          </button>
          <button onClick={onEdit} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900">Edit</button>
          <button onClick={onDelete} title="Delete" className="p-2 text-gray-400 hover:text-red-600">
            <Trash2 size={16} />
          </button>
          <button onClick={onToggle} className="p-2 text-gray-500 hover:text-gray-800">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Signing Secret</h4>
              <div className="flex items-center gap-2">
                <button onClick={onRotateSecret} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
                  <RefreshCw size={12} /> Rotate
                </button>
              </div>
            </div>
            {visibleSecret ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-white p-2 border border-gray-200 rounded break-all">{visibleSecret}</code>
                <button onClick={() => onCopy(visibleSecret)} className="p-2 text-gray-500 hover:text-primary-600">
                  <Copy size={14} />
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">Secret hidden. Rotate to see a new one (old value becomes invalid).</p>
            )}
            <p className="text-[11px] text-gray-500 mt-1">
              Every request includes <code>X-CRM-Signature: sha256=...</code>. Verify it server-side with HMAC-SHA256 of the raw body.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Recent Deliveries</h4>
            {deliveries.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No deliveries yet. Send a test or trigger an event.</p>
            ) : (
              <div className="bg-white border border-gray-200 rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left p-2">When</th>
                      <th className="text-left p-2">Event</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Code</th>
                      <th className="text-left p-2">Attempts</th>
                      <th className="text-left p-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.slice(0, 20).map((d) => (
                      <tr key={d._id} className="border-t border-gray-100">
                        <td className="p-2 text-gray-600 whitespace-nowrap">{new Date(d.createdAt).toLocaleString()}</td>
                        <td className="p-2"><code className="text-[10px]">{d.event}</code></td>
                        <td className="p-2"><StatusBadge status={d.status} /></td>
                        <td className="p-2 text-gray-700">{d.responseCode || '-'}</td>
                        <td className="p-2 text-gray-700">{d.attempts}</td>
                        <td className="p-2 text-gray-500">{d.durationMs ? `${d.durationMs}ms` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookSettings;
